// src/components/shared/StockDrawingUpload.tsx
//
// Drag-drop upload for a STOCK drawing (DWG/DXF required, PDF optional). Posts to the
// existing /api/analyze-files endpoint via the shared analyzeFiles() service
// — the same call the main part-drawing upload uses. The endpoint has no
// "stock vs part" flag, so we just label the resulting geometry locally.
//
// Styling mirrors AgentQuestionBlock's file_upload branch (cyan dashed border,
// JetBrains Mono labels) so the surfaces feel consistent.

import { useState } from 'react'
import { Upload } from 'lucide-react'
import { analyzeFiles } from '../../services/api'

interface Props {
  onUploaded(geometry: Record<string, unknown>, fileName: string): void
  /** Currently unused — kept for future per-unit display tweaks. */
  units?: 'mm' | 'in'
}

export default function StockDrawingUpload({ onUploaded }: Props) {
  const [error, setError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)

  const handleFile = async (file: File) => {
    setError(null)
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (ext === 'step' || ext === 'stp') {
      setError('STEP files are not supported — upload a DWG or DXF drawing (PDF optional).')
      return
    }
    setIsUploading(true)
    setFileName(file.name)
    try {
      const result = await analyzeFiles([file])
      const geometry = result.extraction.geometry
      if (!geometry) {
        throw new Error('No geometry could be extracted from this file')
      }
      onUploaded(geometry, file.name)
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'unknown error'
      setError(`Stock drawing parse failed: ${message}`)
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="space-y-2">
      <label
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false) }}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragging(false)
          const f = e.dataTransfer.files[0]
          if (f) void handleFile(f)
        }}
        className={`
          flex flex-col items-center justify-center gap-2
          w-full rounded-lg cursor-pointer h-28 px-4
          border-2 border-dashed transition-all duration-200
          ${isDragging
            ? 'border-cyan-400 bg-cyan-500/10 shadow-[0_0_20px_rgba(6,182,212,0.3)]'
            : 'border-cyan-500/30 bg-white/[0.02] hover:border-cyan-500/50 hover:bg-white/[0.04]'
          }
          ${isUploading ? 'opacity-60 pointer-events-none' : ''}
        `}
      >
        <input
          type="file"
          accept=".dxf,.dwg,.DXF,.DWG,.pdf,.PDF"
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void handleFile(f)
          }}
        />
        <Upload className={`w-5 h-5 transition-colors ${isDragging ? 'text-cyan-400' : 'text-slate-500'}`} />
        <span className={`font-mono text-xs transition-colors ${isDragging ? 'text-cyan-300' : 'text-slate-400'}`}>
          {isUploading ? 'Analyzing stock drawing…' : 'Drop stock drawing here or click to browse'}
        </span>
        <span className="font-mono text-[10px] text-slate-600 tracking-widest uppercase">
          DWG · DXF · PDF (optional)
        </span>
      </label>

      {fileName && !error && (
        <div className="font-mono text-[10px] text-slate-400">{fileName}</div>
      )}
      {error && (
        <div className="font-mono text-[10px] text-red-400">{error}</div>
      )}
    </div>
  )
}
